import React, { useEffect, useMemo, useState } from 'react';
import { folioService } from '../../../services/folioService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';
import { KIND } from '../FundamentalsReviewPage';

// Folio → Review → "All companies": the CROSS-COMPANY "Needs you" queue (owner 2026-10-26: "we have multiple stocks —
// it doesn't show all the waiting-on-you ones"). `GET review/queue/` lists every covered company's questions that
// need the owner, each with the stage's reason, the recommendation the record holds (the investigator's, else the
// option every stored answer points at — never invented) and its age. Grouped by kind by default (collapsible, with
// counts) or flat; a kind filter (pills) and a company filter; sortable by every column through the shared primitive
// (a table from 640 px, a card list with a sort control on a phone). Every row opens that question on its company's
// review page (`?tab=review&ticker=T&q=<id>`) — the per-company review is untouched.

const COLS = [
  { key: 'company', type: 'string', get: (r) => r.ticker },
  { key: 'kind', type: 'string', get: (r) => KIND[r.kind]?.label || r.kind },
  { key: 'title', type: 'string', get: (r) => r.title },
  { key: 'recommendation', type: 'string', get: (r) => r.recommendation?.text || '' },
  { key: 'age', type: 'number', get: (r) => r.age_days ?? 0 },
];
const SORT_LABEL = { company: 'Company', kind: 'Kind', title: 'Question', recommendation: 'Recommendation', age: 'Age' };

const ageText = (d) => (d == null ? '—' : d === 0 ? 'today' : d === 1 ? '1 day' : `${d} days`);
const kindLabel = (k) => KIND[k]?.label || k;

function KindChip({ kind }) {
  const k = KIND[kind] || { label: kind, cls: 'bg-gray-500/10 text-gray-600 dark:text-gray-300' };
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] uppercase tracking-wide ${k.cls}`}>{k.label}</span>;
}

function Recommendation({ r, long = false }) {
  const rec = r.recommendation;
  if (!rec) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">{r.investigator_pending ? 'none yet — the investigator has not looked' : 'none'}</span>;
  }
  const who = rec.source === 'investigator' ? 'investigator' : `${rec.support || ''} answers`.trim();
  return (
    <span className="text-xs">
      <span className={rec.verified ? 'text-sky-700 dark:text-sky-300' : 'text-amber-700 dark:text-amber-300'}>{rec.text}</span>
      <span className="text-gray-500 dark:text-gray-400"> · {who}{rec.verified ? '' : ', a check failed'}</span>
      {long && rec.line && <span className="block text-gray-500 dark:text-gray-400">{rec.line}</span>}
    </span>
  );
}

function Pill({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={`min-h-[44px] whitespace-nowrap rounded-full border px-3 py-1 text-xs uppercase tracking-wide sm:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 ${on
        ? 'border-brand-gold text-brand-gold' : 'border-gray-300 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>
      {children}
    </button>
  );
}

function Table({ rows, sort, onOpen }) {
  const { toggle, indicator } = sort;
  return (
    <div className="scrollbar-thin hidden overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800 sm:block">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800">
            <SortableTh label="Company" colKey="company" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
            <SortableTh label="Kind" colKey="kind" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
            <SortableTh label="Question" colKey="title" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
            <SortableTh label="Recommendation" colKey="recommendation" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
            <SortableTh label="Age" colKey="age" toggle={toggle} indicator={indicator} className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r)} tabIndex={0} role="link"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(r); } }}
              title={`Open ${r.ticker}-${r.id} on its review page`}
              className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-brand-gold/5 focus:bg-brand-gold/5 focus:outline-none dark:border-gray-800/60">
              <td className="px-3 py-2 align-top">
                <span className="font-mono text-gray-900 dark:text-gray-100">{r.ticker}</span>
                <span className="block max-w-[160px] truncate text-[11px] text-gray-500 dark:text-gray-400">{r.company}</span>
              </td>
              <td className="px-3 py-2 align-top"><KindChip kind={r.kind} /></td>
              <td className="min-w-[260px] px-3 py-2 align-top">
                <span className="text-gray-900 dark:text-gray-100">{r.title}</span>
                <span className="block text-[11px] text-amber-700 dark:text-amber-300">{r.why}</span>
              </td>
              <td className="min-w-[200px] px-3 py-2 align-top"><Recommendation r={r} /></td>
              <td className="whitespace-nowrap px-3 py-2 text-right align-top tabular-nums text-gray-700 dark:text-gray-300">{ageText(r.age_days)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CardList({ rows, onOpen }) {
  return (
    <div className="space-y-2 sm:hidden">
      {rows.map((r) => (
        <button type="button" key={r.id} onClick={() => onOpen(r)}
          className="block w-full rounded-xl border border-gray-200 p-3 text-left hover:border-brand-gold/60 dark:border-gray-800">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-gray-500/10 px-2 py-0.5 font-mono text-[11px] text-gray-800 dark:text-gray-200">{r.ticker}</span>
            <KindChip kind={r.kind} />
            <span className="ml-auto text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{ageText(r.age_days)}</span>
          </div>
          <div className="mt-1.5 text-sm text-gray-900 dark:text-gray-100">{r.title}</div>
          <div className="text-[11px] text-amber-700 dark:text-amber-300">{r.why}</div>
          <div className="mt-1"><Recommendation r={r} /></div>
        </button>
      ))}
    </div>
  );
}

function Group({ kind, rows, open, onToggle, sort, onOpen }) {
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
        <span className="w-3 text-gray-400">{open ? '▾' : '▸'}</span>
        <span className="font-serif text-base text-gray-900 dark:text-gray-100">{kindLabel(kind)}</span>
        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-mono text-[11px] tabular-nums text-amber-700 dark:text-amber-300">{rows.length}</span>
      </button>
      {open && (
        <div className="border-t border-gray-200 p-2 dark:border-gray-800 sm:p-3">
          <Table rows={rows} sort={sort} onOpen={onOpen} />
          <CardList rows={rows} onOpen={onOpen} />
        </div>
      )}
    </section>
  );
}

export default function ReviewQueue({ onOpen }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [kind, setKind] = useState('all');
  const [company, setCompany] = useState('all');
  const [grouped, setGrouped] = useState(true);
  const [closed, setClosed] = useState({});

  useEffect(() => {
    let live = true;
    folioService.getQueue('needs_you')
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setErr('Could not load the queue.'); });
    return () => { live = false; };
  }, []);

  const items = useMemo(() => data?.items || [], [data]);
  const rows = useMemo(() => items.filter((r) => (kind === 'all' || r.kind === kind) && (company === 'all' || r.ticker === company)),
    [items, kind, company]);
  const sort = useSortableRows(rows, COLS);
  const { sorted, sortKey, sortDir, toggle } = sort;
  const kinds = useMemo(() => Object.entries(data?.by_kind || {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])), [data]);
  const groups = useMemo(() => {
    const by = new Map();
    sorted.forEach((r) => { if (!by.has(r.kind)) by.set(r.kind, []); by.get(r.kind).push(r); });
    return [...by.entries()].sort((a, b) => b[1].length - a[1].length || kindLabel(a[0]).localeCompare(kindLabel(b[0])));
  }, [sorted]);

  if (err) return <div className="text-sm text-red-600 dark:text-red-400">{err}</div>;
  if (!data) return <div className="text-sm text-gray-500">Loading the queue…</div>;
  const open = (r) => onOpen(r.ticker, r.id);
  const waiting = data.counts?.waiting || 0;
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {data.total === 0 ? 'Nothing needs you across the covered companies.'
          : `${data.total} question${data.total === 1 ? '' : 's'} need${data.total === 1 ? 's' : ''} you across ${data.companies.length} compan${data.companies.length === 1 ? 'y' : 'ies'}`}
        {waiting ? ` · ${waiting} more waiting for the answerer` : ''}
      </div>
      {data.total > 0 && (
        <>
          <div className="scrollbar-thin flex gap-2 overflow-x-auto pb-1">
            <Pill on={kind === 'all'} onClick={() => setKind('all')}>All {data.total}</Pill>
            {kinds.map(([k, n]) => <Pill key={k} on={kind === k} onClick={() => setKind(k)}>{kindLabel(k)} {n}</Pill>)}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label htmlFor="queue-company" className="text-[11px] uppercase tracking-wider text-gray-500">Company</label>
            <select id="queue-company" value={company} onChange={(e) => setCompany(e.target.value)}
              className="min-h-[44px] max-w-[60vw] rounded-lg border border-gray-300 bg-transparent px-2 py-1 text-xs sm:min-h-0 dark:border-gray-700 dark:bg-[#161b22] dark:text-gray-100">
              <option value="all">All companies ({data.total})</option>
              {data.companies.map((c) => <option key={c.ticker} value={c.ticker}>{c.ticker} · {c.name} ({c.n})</option>)}
            </select>
            <span className="inline-flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700" role="group" aria-label="Layout">
              {[['grouped', true], ['flat', false]].map(([lab, g]) => (
                <button type="button" key={lab} onClick={() => setGrouped(g)} aria-pressed={grouped === g}
                  className={`min-h-[44px] px-2.5 py-1 uppercase tracking-wide sm:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 ${grouped === g ? 'bg-brand-gold/10 text-brand-gold' : 'text-gray-500 dark:text-gray-400'}`}>
                  {lab === 'grouped' ? 'By kind' : 'Flat'}
                </button>
              ))}
            </span>
            <span className="flex items-center gap-1 sm:hidden">
              <label htmlFor="queue-sort" className="text-[11px] uppercase tracking-wider text-gray-500">Sort</label>
              <select id="queue-sort" value={sortKey || ''} onChange={(e) => { if (e.target.value) toggle(e.target.value); }}
                className="min-h-[44px] rounded-lg border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700 dark:bg-[#161b22] dark:text-gray-100 sm:min-h-0">
                <option value="">As listed</option>
                {COLS.map((c) => <option key={c.key} value={c.key}>{SORT_LABEL[c.key]}</option>)}
              </select>
              {sortKey && (
                <button type="button" onClick={() => toggle(sortKey)} aria-label="Flip the sort direction"
                  className="min-h-[44px] min-w-[44px] rounded-lg border border-gray-300 px-2 py-1 text-brand-gold dark:border-gray-700 sm:min-h-0 sm:min-w-0">{sortDir === 'asc' ? '▲' : '▼'}</button>
              )}
            </span>
          </div>
          {!sorted.length && <div className="text-sm text-gray-500">No question matches these filters.</div>}
          {grouped ? groups.map(([k, rs]) => (
            <Group key={k} kind={k} rows={rs} open={!closed[k]} onToggle={() => setClosed((c) => ({ ...c, [k]: !c[k] }))}
              sort={sort} onOpen={open} />
          )) : sorted.length > 0 && (
            <>
              <Table rows={sorted} sort={sort} onOpen={open} />
              <CardList rows={sorted} onOpen={open} />
            </>
          )}
        </>
      )}
    </div>
  );
}
